// Admin ▸ Equipment Rates — upload, preview and publish the equipment rate sheet.
//
// The equipment manager supplies a new sheet roughly twice a year (they expect the next
// one late fall / early winter). Before this tab, loading one meant hand-writing a
// migration, so this exists to make the routine revision a file upload.
//
// The publish is a REPLACE: superseded rows are set is_active = 0, never
// deleted, so equipment requests priced against an older sheet still resolve
// the rate they were quoted at.
//
// ⚠️ The preview is the whole point of this screen. Publishing the 2026 sheet
// would silently have dropped the trench-shoring rows priced from a separate
// United Rentals quote, because they are simply absent from the equipment manager's fleet
// sheet. So anything currently active whose category the upload does not
// mention is listed as an explicit keep/drop decision rather than quietly
// deactivated.

import { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  equipmentRatesApi, usersApi,
  type EquipmentRate, type ImportRatesResult,
} from '@/lib/apiClient';
import {
  parseRateSheet, orphanCategoryCounts, countSuperseded,
  type ParsedRateSheet,
} from './equipmentRateSheet';

// ─── Module-local styles ──────────────────────────────────────────────────────
// Deliberately local: the shared classes in this app live inside the component
// that owns them, and reusing another module's class name renders unstyled.

const TH: React.CSSProperties = {
  padding: '7px 10px', textAlign: 'left', fontWeight: 600,
  fontSize: 'var(--ew-fs-caption)', letterSpacing: 'var(--ew-ls-caption)',
  textTransform: 'uppercase', color: 'var(--ew-n-500)',
  background: 'var(--ew-n-50)', borderBottom: '1px solid var(--ew-n-200)',
  whiteSpace: 'nowrap',
};
const TD: React.CSSProperties = {
  padding: '7px 10px', borderBottom: '1px solid var(--ew-n-100)',
  fontSize: 'var(--ew-fs-body-sm)', verticalAlign: 'middle',
};
const TABLE: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', background: '#fff',
  border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-lg)',
  overflow: 'hidden',
};
const PANEL: React.CSSProperties = {
  border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-lg)',
  background: '#fff', padding: 16, marginBottom: 16,
};
const NOTE: React.CSSProperties = {
  fontSize: 'var(--ew-fs-body-sm)', color: 'var(--ew-n-600)', lineHeight: 1.5,
};

const money = (n: number | null) =>
  n != null
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 })
    : '—';

const spec = (capacityRange: string | null, unit: string | null) =>
  capacityRange ? `${capacityRange} ${unit ?? ''}`.trim() : '—';

export default function EquipmentRatesTab() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => usersApi.me(),
    staleTime: 5 * 60 * 1000,
  });
  // Mirrors @RequirePermission('equipment_rates.manage') on the import route;
  // Admin bypasses the guard server-side. Gating here is presentation only.
  //
  // ⏳ The permission branch is currently unreachable: this tab is only offered to
  // Admin (visibleAdminTabs), and /admin only opens for Admin and for
  // feedback.manage. So someone holding equipment_rates.manage on a custom role is
  // never shown it — redirected away from /admin, or, if they also hold
  // feedback.manage, shown the Feedback tab alone. The check is kept because it is
  // the correct mirror of the API, and
  // because the obvious next step is letting the rates' owner — who holds
  // EquipmentManager — publish their own sheet. The per-tab gate now exists, but
  // opening this tab to a non-admin is still a change to make on its own: it
  // needs its own route entry and tab allow-list entry, and getting it wrong
  // exposes user and role management to a non-admin. Feedback's guard spec
  // (feedback-admin-access.spec.ts) is the model for proving it did not.
  const canManage = (me?.roles.includes('Admin') ?? false)
    || (me?.permissions.includes('equipment_rates.manage') ?? false);

  // ⚠️ Publishing is gated on this query having SUCCEEDED, not merely settled.
  // `active` falls back to [] while it is pending or after it fails, and every
  // safeguard on this screen is computed from it: an empty list yields no
  // orphan categories, an empty preserveCategories, and a reassuring "0 rows
  // superseded". A full replace fired in that window would deactivate the whole
  // catalogue — including the curated rows the preview exists to protect —
  // having shown the admin nothing at all.
  //
  // isFetching matters as much as isSuccess. This shares a query key with
  // RatePicker, which caches for ten minutes — so arriving here from the
  // Equipment page hands us `isSuccess: true` against up-to-ten-minute-old rows
  // while the refetch is still in flight. Publishing in that gap computes
  // preserveCategories from stale data and can supersede a category added since.
  const {
    data: active = [], isPending: ratesPending, isSuccess: ratesLoaded,
    isFetching: ratesFetching, isError: ratesFailed, error: ratesError,
    refetch: refetchRates,
  } = useQuery<EquipmentRate[]>({
    queryKey: ['equipment-rates', { activeOnly: true }],
    queryFn: () => equipmentRatesApi.list({ activeOnly: true }),
  });

  /** Live rows are loaded AND current — the only state in which a publish is safe. */
  const ratesCurrent = ratesLoaded && !ratesFetching;

  const [parsed, setParsed]     = useState<ParsedRateSheet | null>(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [rateYear, setRateYear] = useState(() => new Date().getFullYear());
  const [replaceAll, setReplaceAll] = useState(true);
  const [dropped, setDropped]   = useState<Set<string>>(new Set());
  const [result, setResult]     = useState<ImportRatesResult | null>(null);

  // Categories that are active today but absent from the upload. Under a full
  // replace these disappear unless kept, so each is an explicit decision.
  const orphanCategories = useMemo(
    () => (parsed ? orphanCategoryCounts(active, parsed.rows) : []),
    [parsed, active],
  );

  const preserveCategories = useMemo(
    () => orphanCategories.map(([c]) => c).filter((c) => !dropped.has(c)),
    [orphanCategories, dropped],
  );

  // What the publish will actually deactivate, computed the same way the API
  // will, so the number shown is the number that happens.
  const willDeactivate = useMemo(
    () => countSuperseded(active, { replaceAll, rateYear, preserveCategories }),
    [active, preserveCategories, replaceAll, rateYear],
  );

  const reset = () => {
    setParsed(null); setFileName(''); setParseError(null);
    setDropped(new Set()); setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  async function onFile(file: File) {
    setParseError(null); setResult(null); setDropped(new Set());
    setFileName(file.name);
    try {
      const sheet = parseRateSheet(await file.arrayBuffer());
      setParsed(sheet);
    } catch (err) {
      setParsed(null);
      setParseError(err instanceof Error ? err.message : String(err));
    }
  }

  const publish = useMutation({
    mutationFn: () =>
      equipmentRatesApi.bulkImport({
        rateYear,
        supersede: replaceAll ? 'all-active' : 'same-year',
        preserveCategories,
        // sourceRow is for the preview only — the API does not take it.
        rows: (parsed?.rows ?? []).map(({ sourceRow: _sourceRow, ...row }) => row),
      }),
    onSuccess: (res) => {
      setResult(res);
      setParsed(null); setFileName('');
      if (fileRef.current) fileRef.current.value = '';
      qc.invalidateQueries({ queryKey: ['equipment-rates'] });
    },
  });

  // Declared after the mutation it reads — `publish` is block-scoped, so
  // hoisting this above it is a temporal-dead-zone crash for any admin who
  // actually gets past `canManage`.
  //
  // ratesLoaded is not optional here: see the note on the query above.
  const canPublish =
    canManage && ratesCurrent && !publish.isPending && (parsed?.rows.length ?? 0) > 0;

  // ─── Current sheet ──────────────────────────────────────────────────────────

  const summary = useMemo(() => {
    const years = new Map<number, number>();
    const cats = new Set<string>();
    for (const r of active) {
      years.set(r.rateYear, (years.get(r.rateYear) ?? 0) + 1);
      cats.add(r.category);
    }
    return {
      total: active.length,
      categories: cats.size,
      years: [...years.entries()].sort(([a], [b]) => a - b),
    };
  }, [active]);

  return (
    <div>
      <p style={{ ...NOTE, marginBottom: 16 }}>
        Upload the rate sheet workbook, check the preview, then publish. Publishing
        supersedes the rows it replaces — they are marked inactive, never deleted, so
        equipment already requested keeps the rate it was quoted at.
      </p>

      {/* ── What is live now ───────────────────────────────────────────────── */}
      <div style={PANEL}>
        <h3 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body)/1 var(--ew-font)', marginBottom: 10 }}>
          Live rate sheet
        </h3>
        {ratesPending ? (
          <div style={NOTE}>Loading…</div>
        ) : ratesFailed ? (
          <div style={{ ...NOTE, color: 'var(--ew-danger, #b91c1c)' }}>
            Could not load the live rate sheet
            {ratesError instanceof Error ? `: ${ratesError.message}` : ''}. Publishing is
            blocked until it loads — without it there is no way to show what a publish
            would supersede.{' '}
            <button
              type="button"
              onClick={() => void refetchRates()}
              style={{
                padding: '2px 10px', border: '1px solid var(--ew-n-300)',
                borderRadius: 'var(--ew-r-md)', background: '#fff',
                fontSize: 'var(--ew-fs-body-sm)', cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        ) : (
          <div style={NOTE}>
            <strong>{summary.total}</strong> active {summary.total === 1 ? 'row' : 'rows'} across{' '}
            <strong>{summary.categories}</strong> categories
            {summary.years.length > 0 && (
              <>
                {' '}— {summary.years.map(([y, n]) => `${n} tagged ${y}`).join(', ')}
              </>
            )}
            .
            {summary.years.length > 1 && (
              <div style={{ marginTop: 6, color: 'var(--ew-warn, #92400e)' }}>
                More than one rate year is active. The rate picker shows every active row
                regardless of year, so these appear side by side.
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Upload ─────────────────────────────────────────────────────────── */}
      <div style={PANEL}>
        <h3 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body)/1 var(--ew-font)', marginBottom: 10 }}>
          Upload a new sheet
        </h3>
        <p style={{ ...NOTE, marginBottom: 10 }}>
          Expected columns: <code>Category</code>, <code>Equipment type/model</code>,{' '}
          <code>Class</code>, <code>Daily</code>, <code>Weekly</code>, <code>Monthly</code>.
          A title row above the header is fine — the header is found by name.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          disabled={!canManage}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }}
          style={{ fontSize: 'var(--ew-fs-body-sm)' }}
        />
        {!canManage && (
          <div style={{ ...NOTE, marginTop: 8, color: 'var(--ew-n-500)' }}>
            You need the <em>Manage the equipment rate catalog</em> permission to publish a sheet.
          </div>
        )}
        {fileName && <div style={{ ...NOTE, marginTop: 8 }}>Loaded <strong>{fileName}</strong></div>}
        {parseError && (
          <div style={{ ...NOTE, marginTop: 8, color: 'var(--ew-danger, #b91c1c)' }}>
            Could not read that file: {parseError}
          </div>
        )}
      </div>

      {result && (
        <div style={{ ...PANEL, borderColor: 'var(--ew)', background: 'var(--ew-dim, #f0fdf4)' }}>
          <strong>Published.</strong> Inserted {result.inserted} rows, superseded {result.deactivated}.
        </div>
      )}

      {/* ── Preview & publish ──────────────────────────────────────────────── */}
      {parsed && (
        <>
          <div style={PANEL}>
            <h3 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body)/1 var(--ew-font)', marginBottom: 10 }}>
              Publish settings
            </h3>

            <label style={{ ...NOTE, display: 'block', marginBottom: 10 }}>
              Rate year{' '}
              <input
                type="number"
                value={rateYear}
                min={2000}
                max={2100}
                onChange={(e) => setRateYear(Number(e.target.value))}
                style={{
                  padding: '5px 8px', border: '1px solid var(--ew-n-300)',
                  borderRadius: 'var(--ew-r-md)', fontSize: 'var(--ew-fs-body-sm)',
                  width: 90, marginLeft: 6,
                }}
              />
            </label>

            <label style={{ ...NOTE, display: 'block', marginBottom: 6 }}>
              <input type="radio" checked={replaceAll} onChange={() => setReplaceAll(true)} />{' '}
              <strong>Replace the whole sheet</strong> — supersede every active row, whatever
              year it carries. This is what publishing a new annual sheet means.
            </label>
            <label style={{ ...NOTE, display: 'block' }}>
              <input type="radio" checked={!replaceAll} onChange={() => setReplaceAll(false)} />{' '}
              <strong>Replace only {rateYear}</strong> — leave other years alone. For correcting
              a sheet already published. Rows from other years stay visible in the picker.
            </label>
          </div>

          {orphanCategories.length > 0 && (
            <div style={{ ...PANEL, borderColor: 'var(--ew-warn-border, #fcd34d)' }}>
              <h3 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body)/1 var(--ew-font)', marginBottom: 8 }}>
                Active categories this upload does not mention
              </h3>
              <p style={{ ...NOTE, marginBottom: 10 }}>
                These are live today but absent from the sheet. Keep them and they stay in the
                picker alongside the new rows; untick to let this publish supersede them.
              </p>
              {orphanCategories.map(([cat, n]) => (
                <label key={cat} style={{ ...NOTE, display: 'block', marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    checked={!dropped.has(cat)}
                    onChange={(e) => {
                      const next = new Set(dropped);
                      if (e.target.checked) next.delete(cat); else next.add(cat);
                      setDropped(next);
                    }}
                  />{' '}
                  Keep <strong>{cat}</strong> ({n} {n === 1 ? 'row' : 'rows'})
                </label>
              ))}
            </div>
          )}

          {parsed.errors.length > 0 && (
            <div style={{ ...PANEL, borderColor: 'var(--ew-danger, #b91c1c)' }}>
              <h3 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body)/1 var(--ew-font)', marginBottom: 8, color: 'var(--ew-danger, #b91c1c)' }}>
                {parsed.errors.length} row{parsed.errors.length === 1 ? '' : 's'} could not be read and will be skipped
              </h3>
              <ul style={{ ...NOTE, paddingLeft: 18, margin: 0 }}>
                {parsed.errors.map((e, i) => <li key={i}>Row {e.sourceRow}: {e.message}</li>)}
              </ul>
            </div>
          )}

          {parsed.warnings.length > 0 && (
            <div style={{ ...PANEL, borderColor: 'var(--ew-warn-border, #fcd34d)' }}>
              <h3 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body)/1 var(--ew-font)', marginBottom: 8 }}>
                {parsed.warnings.length} row{parsed.warnings.length === 1 ? '' : 's'} worth a look
              </h3>
              <p style={{ ...NOTE, marginBottom: 8 }}>These still import — check them against the source sheet.</p>
              <ul style={{ ...NOTE, paddingLeft: 18, margin: 0 }}>
                {parsed.warnings.map((w, i) => <li key={i}>Row {w.sourceRow}: {w.message}</li>)}
              </ul>
            </div>
          )}

          <div style={PANEL}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={NOTE}>
                Publishing inserts <strong>{parsed.rows.length}</strong> rows tagged{' '}
                <strong>{rateYear}</strong> and supersedes <strong>{willDeactivate}</strong> of the{' '}
                {active.length} active now
                {preserveCategories.length > 0 && (
                  <> — keeping {preserveCategories.join(', ')}</>
                )}
                .
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={reset}
                  disabled={publish.isPending}
                  style={{
                    padding: '7px 14px', border: '1px solid var(--ew-n-300)',
                    borderRadius: 'var(--ew-r-md)', background: '#fff',
                    fontSize: 'var(--ew-fs-body-sm)', cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!canPublish}
                  onClick={() => publish.mutate()}
                  style={{
                    padding: '7px 14px', border: '1px solid var(--ew-dark)',
                    borderRadius: 'var(--ew-r-md)', background: 'var(--ew-dark)',
                    color: '#fff', fontSize: 'var(--ew-fs-body-sm)',
                    cursor: canPublish ? 'pointer' : 'not-allowed',
                    opacity: canPublish ? 1 : 0.6,
                  }}
                >
                  {publish.isPending ? 'Publishing…' : `Publish ${parsed.rows.length} rows`}
                </button>
              </div>
            </div>

            {!ratesCurrent && (
              <div style={{ ...NOTE, marginBottom: 10, color: 'var(--ew-danger, #b91c1c)' }}>
                {ratesPending || ratesFetching
                  ? 'Refreshing the live rate sheet — publishing is enabled once it is current.'
                  : 'Publishing is blocked until the live rate sheet loads — the counts above ' +
                    'cannot be trusted without it, and a replace could supersede rows this ' +
                    'preview never showed you.'}
              </div>
            )}

            {publish.isError && (
              <div style={{ ...NOTE, marginBottom: 10, color: 'var(--ew-danger, #b91c1c)' }}>
                Publish failed: {publish.error instanceof Error ? publish.error.message : 'unknown error'}
                . Nothing was changed — the import runs in a transaction.
              </div>
            )}

            <div style={{ maxHeight: 420, overflow: 'auto' }}>
              <table style={TABLE}>
                <thead>
                  <tr>
                    <th style={TH}>Category</th>
                    <th style={TH}>Model</th>
                    <th style={TH}>Class</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Day</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Week</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Month</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.map((r) => (
                    <tr key={r.sourceRow}>
                      <td style={TD}>{r.category}</td>
                      <td style={TD}>{r.model}</td>
                      <td style={TD}>{spec(r.capacityRange, r.unit)}</td>
                      <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(r.dayRate)}</td>
                      <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(r.weekRate)}</td>
                      <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(r.monthRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
